import { yupResolver } from "@hookform/resolvers/yup";
import { TextArea } from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import useToast from "../../../../hooks/useToast";
import { useUpdateConsent } from "../../hooks/mutations/reports.mutations";
import { ConsentStatus } from "../../types/reports.types";

interface RequestConsentCorrectionFormProps {
    consentId: string;
    onFormSubmit: () => void;
    queryKey?: string;
}

interface RequestConsentCorrectionFormValues {
    correctionReason: string;
}

const schema = yup.object({
    correctionReason: yup
        .string()
        .trim()
        .required("Correction reason is required")
        .max(2000, "Correction reason cannot be longer than 2000 characters"),
});

export default function RequestConsentCorrectionForm({
    consentId,
    onFormSubmit,
    queryKey = "consent-reports",
}: RequestConsentCorrectionFormProps) {
    const queryClient = useQueryClient();
    const updateConsent = useUpdateConsent();
    const { toast } = useToast();

    const {
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<RequestConsentCorrectionFormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const onSubmit = (data: RequestConsentCorrectionFormValues) => {
        updateConsent.mutate(
            {
                consentId,
                data: {
                    status: ConsentStatus.NEEDS_CORRECTION,
                    correction_reason: data.correctionReason,
                },
            },
            {
                onSuccess: async () => {
                    await queryClient.invalidateQueries([queryKey]);
                    toast({
                        mode: "success",
                        message: "Correction request emailed to the client",
                    });
                    onFormSubmit();
                },
                onError: () => {
                    toast({
                        mode: "error",
                        message:
                            "Could not request a consent correction at this time",
                    });
                },
            }
        );
    };

    return (
        <form
            id="request-consent-correction"
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
        >
            <p className="text-sm text-gray-600">
                Add the correction the client needs to make. Mantle will email
                this reason to the client with a link to their forms.
            </p>
            <TextArea
                {...register("correctionReason")}
                label="Correction reason"
                rows={4}
                maxLength={2000}
                hasError={!!errors.correctionReason}
                errorText={errors.correctionReason?.message}
            />
        </form>
    );
}
